/**
 * @file Planboard grid component
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { AfterViewInit, OnDestroy } from '@angular/core';
import { DomainService } from '../../domain.service';
import { AlertsService } from '../../core/alerts.service';
import { LocalStorageService } from '../../core/local-storage.service';
import { DataSet } from '../../core/helpers/data-set';
import { SortDescriptor } from '../../core/helpers/sort-descriptor';
import { SortDirection } from '../../core/helpers/sort-direction';
import { PlanboardGridBarComponent } from '../planboard-grid-bar/planboard-grid-bar.component';
import { GridService } from '../grid.service';
import { SettingsService } from './../../settings.service';
import { GridBaseComponent } from '../grid-base/grid-base.component';
/**
 * Planboard grid component.
 */
var PlanboardGridComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PlanboardGridComponent, _super);
    function PlanboardGridComponent(domainService, localStorageService, gridService, settingsService, alertsService) {
        var _this = _super.call(this, domainService, localStorageService, gridService, alertsService) || this;
        _this.settingsService = settingsService;
        _this.alertsService = alertsService;
        /** List of dynamic query parameters. */
        _this.dynamicParameters = [];
        // Important: visiblity per layer is also per grid type (agenda and planboard)
        _this.visibilityPerLayer = {
            'workpatterns': _this.localStorageService.readProperty('local', 'grid-show-workpatterns', true, [true, false]),
            'availabilities': _this.localStorageService.readProperty('local', 'grid-show-availabilities', false, [true, false]),
            'absences': _this.localStorageService.readProperty('local', 'grid-show-absences', true, [true, false]),
            'assignments': _this.localStorageService.readProperty('local', 'grid-show-assignments', true, [true, false]),
            'appointments': _this.localStorageService.readProperty('local', 'grid-show-appointments', true, [true, false]),
            'attendants': _this.localStorageService.readProperty('local', 'grid-show-attendants', true, [true, false]),
            'drop-targets': _this.localStorageService.readProperty('local', 'grid-show-drop-targets', true, [true, false]),
            'violations': _this.localStorageService.readProperty('local', 'grid-show-violations', false, [true, false]),
            'adhoc': _this.localStorageService.readProperty('local', 'grid-show-adhoc', true, [true, false]),
            'template': _this.localStorageService.readProperty('local', 'grid-show-template', true, [true, false]),
            'forecast': _this.localStorageService.readProperty('local', 'grid-show-forecast', true, [true, false]),
            // Tijdelijk, script + extra, uitgezet zonder dat ejs omvalt.
            // Zie: Lagen kiezer lijkt niet helemaal goed te werken bij forecast opties. #3074
            'script': true,
            'extra': true,
            'dragsources': (_this.isTouchDevice === true) ? false : _this.localStorageService.readProperty('local', 'grid-show-dragsources', true, [true, false])
        };
        _this._resources = new DataSet(_this.domainService);
        return _this;
    }
    Object.defineProperty(PlanboardGridComponent.prototype, "resources", {
        /**
         * Gets the data set of the shown resources
         * @readonly
         * @type {DataSet}
         * @memberOf PlanboardGridComponent
         */
        get: function () {
            return this._resources;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridComponent.prototype, "selectedFilterEntity", {
        /**
         * Gets the entity used as filter for the shown resources.
         * @readonly
         * @type {Entity}
         * @memberOf PlanboardGridComponent
         */
        get: function () {
            if (!this.gridbar || !this.gridbar.filterEntity) {
                return null;
            }
            return this.gridbar.filterEntity;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridComponent.prototype, "selectedTeam", {
        /**
         * Gets selected team when the resource filter is a team.
         * The selected team is used as filter for the service drag sources.
         * @readonly
         * @type {Entity} The selected team.
         * @memberOf PlanboardGridComponent
         */
        get: function () {
            var value = this.selectedFilterEntity;
            if (value && value.entityType.shortName === 'Team') {
                return value;
            }
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridComponent.prototype, "filterEntityOid", {
        /**
         * Gets the oid of the entity used as filter for the shown resources.
         * @readonly
         * @type {Entity}
         * @memberOf PlanboardGridComponent
         */
        get: function () {
            var filterEntity = this.selectedFilterEntity;
            var userChangedFilter = this.gridbar.userChangedFilter;
            this.gridbar.userChangedFilter = false;
            if (!filterEntity) {
                // if there is not a pending user's choice then look in the storage
                if (!userChangedFilter) {
                    var storedFilterOid = this.localStorageService.readProperty('local', 'planboard-filter-entity', undefined);
                    if (storedFilterOid) {
                        return storedFilterOid;
                    }
                }
                return '';
            }
            return filterEntity['Oid'];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridComponent.prototype, "start", {
        /**
         * Gets the horizon start of the displayed planboard.
         * @readonly
         * @type {Date}
         */
        get: function () {
            return this.gridService.startDate.toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridComponent.prototype, "end", {
        /**
         * Gets the horizon end of the displayed planboard
         * @readonly
         * @type {Date}
         */
        get: function () {
            return this.gridService.endDate.toDate();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Respond after Angular initializes the component's views and child views
     */
    PlanboardGridComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        _super.prototype.ngAfterViewInit.call(this);
        this.horizonSupscription = this.gridService.stateChanged.subscribe(function (state) {
            _this.refresh();
        });
    };
    /**
     * Cleanup just before Angular destroys the component.
     * Unsubscribe Observables and detach event handlers to avoid memory leaks.
     */
    PlanboardGridComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        if (this.horizonSupscription) {
            this.horizonSupscription.unsubscribe();
        }
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     */
    PlanboardGridComponent.prototype.afterInitialize = function () {
        var _this = this;
        _super.prototype.afterInitialize.call(this);
        var pluginPairSelectorComponent = this.configuration.components.find(function (item) { return item.component.indexOf('PluginPairSelectorComponent') !== -1; });
        this.pluginPairSource = pluginPairSelectorComponent ? pluginPairSelectorComponent.configuration : null;
        var entityConfig = this.configuration.data;
        this._resources.sourceName = entityConfig.dataSource;
        if (!entityConfig.listSize) {
            // Default page size.
            entityConfig.listSize = 20;
        }
        // Clear the sorting and add sort descriptor from (if found) the settings, otherwise default is Name
        var sortMethod = this.settingsService.settings.frontend.grid.sortMethod;
        this._resources.sorting.length = 0;
        if (sortMethod) {
            this._resources.sorting.push(new SortDescriptor(sortMethod, sortMethod, SortDirection.Ascending, 0));
        }
        else {
            this._resources.sorting.push(new SortDescriptor('Name', 'Name', SortDirection.Ascending, 0));
        }
        this._resources.queryOptions = {
            take: entityConfig.listSize,
            expand: entityConfig.expand,
            parameters: Object.assign({}, entityConfig.parameters)
        };
        // Fix issue #2364
        // Deze window.setTimeout lijkt te voorkomen dat we
        // soms this.gridbar.horizon gaan missen bij de load.
        window.setTimeout(function () {
            DomainService.getDynamicParams(_this._resources.queryOptions, _this.dynamicParameters);
            DomainService.setDynamicParams(_this._resources.queryOptions, _this.dynamicParameters, _this);
            _this._resources.load(entityConfig.entity);
        });
    };
    /**
     * Method to retrieve the requested entities from the backend api.
     * @returns An Observable object that retrieves the requested entities.
     */
    PlanboardGridComponent.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.configuration) {
                            return [2 /*return*/, null];
                        }
                        if (this.dynamicParameters.length === 0) {
                            // Fix: Regelmatig Not Found melding bij openen planbord #3068
                            // DomainService.getDynamicParams is nog niet gedaan.
                            return [2 /*return*/, null];
                        }
                        DomainService.setDynamicParams(this._resources.queryOptions, this.dynamicParameters, this);
                        return [4 /*yield*/, this._resources.refresh()];
                    case 1:
                        result = _a.sent();
                        return [2 /*return*/, result];
                }
            });
        });
    };
    return PlanboardGridComponent;
}(GridBaseComponent));
export { PlanboardGridComponent };
